/*
  Tagalys native card injection
  ==============================
  Replaces Tagalys SDK-rendered product tiles with native theme card HTML,
  fetched per product handle from the Section Rendering API endpoint:
    GET /products/{handle}?view=tagalys-card
    → templates/product.tagalys-card.liquid (layout none, card HTML only)

  This eliminates the CSS specificity war with the Tagalys SDK's injected
  stylesheet. Native card HTML carries the theme's own class names so all
  existing card styles — swatches, vendor, fabric, price, Yotpo reviews —
  apply without overrides.

  Surfaces observed (any .product child of these containers is replaced):
    .tagalys-search-results     — /search results grid
    .tagalys-widget             — recommendation widgets

  Re-runs automatically on Tagalys SDK re-renders (facet / sort / pagination
  trigger DOM mutations that the observer catches).

  Loaded gated on settings.tagalys_enabled from layout/theme.liquid.
*/
(function () {
  "use strict";

  // Locale-aware root (e.g. "/" or "/en-au/") so the product URL resolves.
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  // Standard surfaces: .product tiles replaced with ?view=tagalys-card content.
  // Custom surfaces: .product tiles replaced with a surface-specific <li> template.
  var SURFACE_SELECTORS = [
    ".tagalys-search-results",
    ".tagalys-widget",
  ].join(",");

  var CUSTOM_SURFACES = [];

  function getViewForTile(tile) {
    for (var i = 0; i < CUSTOM_SURFACES.length; i++) {
      if (tile.closest && tile.closest(CUSTOM_SURFACES[i].selector)) {
        return CUSTOM_SURFACES[i].view;
      }
    }
    return "tagalys-card";
  }

  // Cache: handle → resolved HTML string (or null on fetch failure).
  var cache = Object.create(null);
  // Inflight: handle → true while a fetch is pending.
  var inflight = Object.create(null);
  // Tiles waiting for a handle's fetch to complete: handle → [.product elements].
  var pending = Object.create(null);

  // ---- Handle extraction ---------------------------------------------------

  function extractHandle(tile) {
    var link = tile.querySelector("a.product-link, a[href*='/products/']");
    if (!link) return null;
    var m = (link.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ---- Fetch + inject -------------------------------------------------------

  function injectCard(tile, html, view) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, "text/html");

    if (view && view !== "tagalys-card") {
      // Custom surface: swap the .product div out for the native <li> element.
      var li = doc.querySelector("li");
      if (!li || !tile.parentNode) return;
      tile.parentNode.replaceChild(li, tile);
      return;
    }

    // Standard surface: inject card HTML into the tile.
    var card = doc.querySelector("product-card");
    if (!card) return;
    tile.classList.add("exclude-tagalys-css-reset");
    tile.innerHTML = card.outerHTML;
    scheduleYotpoRefresh();
  }

  function fetchAndInject(handle, tile) {
    var view = getViewForTile(tile);
    var cacheKey = handle + ":" + view;

    if (inflight[cacheKey]) return;
    inflight[cacheKey] = true;

    // Cache-bust: Shopify caches ?view= responses at the CDN, so without this
    // the card HTML (prices, sale state) can be served stale after a change.
    fetch(ROOT + "products/" + encodeURIComponent(handle) + "?view=" + view + "&_=" + Date.now(), { cache: "no-store" })
      .then(function (r) {
        return r.ok ? r.text() : Promise.reject(r.status);
      })
      .then(function (html) {
        cache[cacheKey] = html;
        delete inflight[cacheKey];
        var waiting = pending[cacheKey] || [];
        delete pending[cacheKey];
        waiting.forEach(function (item) {
          injectCard(item.tile, html, view);
        });
      })
      .catch(function (err) {
        cache[cacheKey] = null;
        delete inflight[cacheKey];
        delete pending[cacheKey];
        if (err) console.warn("[tagalys-rest] fetch failed for", handle, view, err);
      });
  }

  // ---- Yotpo ----------------------------------------------------------------

  var yotpoTimer = null;
  function scheduleYotpoRefresh() {
    if (yotpoTimer) clearTimeout(yotpoTimer);
    yotpoTimer = setTimeout(function () {
      yotpoTimer = null;
      try {
        if (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.initWidgets) {
          window.yotpoWidgetsContainer.initWidgets();
        } else if (window.yotpo && window.yotpo.refreshWidgets) {
          window.yotpo.refreshWidgets();
        }
      } catch (e) {
        /* Yotpo not ready — a later injection will retry. */
      }
    }, 300);
  }

  // ---- Tile processing ------------------------------------------------------

  // Tiles that have already been replaced carry this marker so re-runs skip them.
  var DONE_ATTR = "data-tgls-native";

  function processTile(tile) {
    if (tile.hasAttribute(DONE_ATTR)) return;
    tile.setAttribute(DONE_ATTR, "1");

    var handle = extractHandle(tile);
    if (!handle) return;

    var view = getViewForTile(tile);
    var cacheKey = handle + ":" + view;

    if (cache[cacheKey] !== undefined) {
      if (cache[cacheKey]) injectCard(tile, cache[cacheKey], view);
      return;
    }

    if (!pending[cacheKey]) pending[cacheKey] = [];
    pending[cacheKey].push({ tile: tile, view: view });
    fetchAndInject(handle, tile);
  }

  function scanSurfaces(root) {
    var surfaces = (root || document).querySelectorAll(SURFACE_SELECTORS);
    if (!surfaces.length) return;
    surfaces.forEach(function (surface) {
      // Tagalys renders each product inside a .product div.
      surface.querySelectorAll(".product:not([" + DONE_ATTR + "])").forEach(processTile);
    });
  }

  // ---- Search filter drawer + sort button -----------------------------------
  // Moves the Tagalys Preact-rendered .filters panel into the native Horizon
  // <dialog> drawer (rendered by snippets/tagalys-search.liquid), and replaces
  // the "Sort by [select]" with a "Sort" button + icon overlaying the native
  // select. The drawer's open/close/ESC/backdrop/scroll-lock are all handled by
  // DialogComponent + the declarative on:click attributes in the markup.

  var SORT_SVG =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path vector-effect="non-scaling-stroke" d="M2.57153 18.8572L6.85725 23.1429M6.85725 23.1429L11.143 18.8572M6.85725 23.1429V0.857178M12.8572 5.14289L17.143 0.857178M17.143 0.857178L21.4287 5.14289M17.143 0.857178V23.1429" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg>";

  function initSearchDrawer() {
    var searchContainer = document.querySelector(".tagalys-search-results");
    if (!searchContainer) return;

    // Drawer markup is rendered by snippets/tagalys-search.liquid. Bail if
    // absent (e.g. Tagalys search disabled) — nothing to wire.
    var drawer = document.getElementById("tagalys-filters-drawer");
    if (!drawer) return;

    var slot = drawer.querySelector(".tagalys-facets-drawer__slot");
    var toggle = document.getElementById("tagalys-filters-toggle");
    var countBadge = drawer.querySelector(".tagalys-facets-drawer__count");
    var applyBtn = drawer.querySelector(".tagalys-facets-drawer__apply");
    var applyOne = applyBtn ? applyBtn.getAttribute("data-tagalys-apply-one") : "";
    var applyOther = applyBtn ? applyBtn.getAttribute("data-tagalys-apply-other") : "";
    var clearBtn = drawer.querySelector(".tagalys-facets-drawer__clear");
    var toggleMoved = false;
    var sortWired = false;
    var clearWired = false;

    // Sync the header count badge (number of applied filters) and the footer
    // "See N products" button (live result count). Applied count is derived from
    // checked checkboxes in the slotted panel; product count from .results-count.
    function syncFilterMeta() {
      var checked = slot ? slot.querySelectorAll(".filter-item input:checked") : [];
      if (countBadge) {
        if (checked.length > 0) {
          countBadge.textContent = checked.length;
          countBadge.hidden = false;
        } else {
          countBadge.hidden = true;
        }
      }
      if (applyBtn && applyOther) {
        var countEl = searchContainer.querySelector(".results-count");
        var num = countEl ? (countEl.textContent.match(/\d[\d,]*/) || [""])[0] : "";
        var template = num === "1" ? applyOne : applyOther;
        applyBtn.textContent = template.replace("__count__", num);
      }
    }

    new MutationObserver(function () {
      // Relocate the pre-rendered Filters button into the Tagalys toolbar (once).
      // Prefer the results-count/filter-toggle row so the query heading (.plp-heading)
      // is left alone as a standalone line above; fall back to .plp-heading.
      if (!toggleMoved && toggle) {
        var toolbar =
          searchContainer.querySelector(".results-count-and-filter-toggle") ||
          searchContainer.querySelector(".plp-heading");
        if (toolbar) {
          toggleMoved = true;
          toolbar.insertBefore(toggle, toolbar.firstChild);
          toggle.hidden = false;
        }
      }

      // Wire the footer "Clear all" once — clicks each checked filter to unset it
      // (Tagalys owns the onChange → re-query for each).
      if (!clearWired && clearBtn && slot) {
        clearWired = true;
        clearBtn.addEventListener("click", function () {
          var checked = slot.querySelectorAll(".filter-item input:checked");
          for (var i = 0; i < checked.length; i++) checked[i].click();
        });
      }

      syncFilterMeta();

      // Move the Preact .filters panel into the drawer slot. Kept permanently
      // slotted — the spike confirmed Preact patches it in place after the move.
      // replaceChildren guards against a transient duplicate on the rare chance
      // the SDK recreates .filters in its own container (defensive backstop).
      if (slot) {
        var filters = searchContainer.querySelector(".filters");
        if (filters && filters.parentNode !== slot) {
          slot.replaceChildren(filters);
        }
      }

      // Sort button (once).
      if (!sortWired) {
        var sortContainer = searchContainer.querySelector(".sort-options-container");
        if (sortContainer) {
          sortWired = true;
          var sortBtn = document.createElement("div");
          sortBtn.className = "tagalys-sort-button";
          sortBtn.innerHTML = "Sort " + SORT_SVG;
          sortContainer.insertBefore(sortBtn, sortContainer.firstChild);
        }
      }
    }).observe(searchContainer, { childList: true, subtree: true });
  }

  // ---- MutationObserver -----------------------------------------------------

  function start() {
    initSearchDrawer();
    scanSurfaces(document);

    new MutationObserver(function (mutations) {
      var needsScan = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) {
            needsScan = true;
            break;
          }
        }
        if (needsScan) break;
      }
      if (needsScan) scanSurfaces(document);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState !== "loading") {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start);
  }
})();
